
import React from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Exhibit } from '@/data';

interface CustomInfoWindowProps {
  exhibit: Exhibit;
  position: google.maps.LatLng;
  map: google.maps.Map | null;
  onClose: () => void;
  onViewExhibit: (id: number) => void;
  language: string;
}

const CustomInfoWindow = ({ 
  exhibit, 
  position, 
  map, 
  onClose, 
  onViewExhibit, 
  language 
}: CustomInfoWindowProps) => {
  const [containerElement, setContainerElement] = React.useState<HTMLDivElement | null>(null);
  const popupRef = React.useRef<HTMLDivElement>(null);
  const clickListenerRef = React.useRef<google.maps.MapsEventListener | null>(null);
  
  React.useEffect(() => {
    // Create a div element that will be positioned over the map
    const div = document.createElement('div');
    setContainerElement(div);
    
    // Clean up on unmount
    return () => {
      setContainerElement(null);
    };
  }, []);
  
  React.useEffect(() => {
    if (!containerElement || !map || !position) return;
    
    // Create an overlay using the Google Maps OverlayView
    const overlay = new google.maps.OverlayView();
    
    overlay.onAdd = function() {
      const panes = overlay.getPanes();
      panes.floatPane.appendChild(containerElement);
    };
    
    overlay.onRemove = function() {
      if (containerElement.parentElement) {
        containerElement.parentElement.removeChild(containerElement);
      }
    };
    
    overlay.draw = function() {
      const projection = overlay.getProjection();
      if (!projection || !popupRef.current) return;
      
      const point = projection.fromLatLngToDivPixel(position);
      if (!point) return;
      
      // Position the popup centered horizontally above the marker
      const offsetX = popupRef.current.offsetWidth / 2;
      const offsetY = popupRef.current.offsetHeight + 10; // Position above the marker with some spacing
      
      popupRef.current.style.left = (point.x - offsetX) + 'px';
      popupRef.current.style.top = (point.y - offsetY) + 'px';
    };
    
    // Add the overlay to the map
    overlay.setMap(map);
    
    // Add event listener to close when clicking elsewhere on the map
    // Store the listener reference for proper cleanup
    if (map) {
      clickListenerRef.current = map.addListener('click', onClose);
    }
    
    // Clean up
    return () => {
      overlay.setMap(null);
      
      // Safe cleanup of event listener
      if (clickListenerRef.current) {
        google.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
    };
  }, [containerElement, map, position, onClose]);
  
  if (!containerElement) return null;
  
  // Use React Portal to render our component into the overlay div
  return createPortal(
    <div 
      ref={popupRef}
      className={`absolute z-50 p-3 bg-white rounded-lg shadow-lg border border-gray-200 min-w-64 max-w-72 ${language === 'en' ? 'text-left' : 'text-right'}`}
      dir={language === 'en' ? 'ltr' : 'rtl'}
      onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
    >
      <button 
        onClick={onClose}
        className={`absolute top-2 ${language === 'en' ? 'right-2' : 'left-2'} w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200`}
        aria-label="Close"
      >
        ×
      </button>
      
       <h3 className="font-bold text-lg text-museum pt-1 whitespace-pre-line">
         {language === 'en' ? exhibit.titleEn : exhibit.titleHe}
       </h3>
      
      <p className="text-sm text-gray-600 my-2">
        {language === 'en' ? 'Location: ' : 'מיקום: '}
        {exhibit.location.room}
      </p>
      
      <Button
        className="w-full mt-2 bg-museum hover:bg-museum-dark"
        onClick={() => onViewExhibit(exhibit.id)}
      >
        {language === 'en' ? 'View Exhibit' : 'צפה במוצג'}
      </Button>
      
      {/* Triangular pointer at bottom of popup */}
      <div 
        className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"
        style={{ filter: 'drop-shadow(0 2px 1px rgba(0,0,0,0.1))' }}
      ></div>
    </div>,
    containerElement
  );
};

export default CustomInfoWindow;
